import React, { useEffect } from 'react';
import { AppProvider } from './context/AppContext';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import HomePage from './pages/HomePage';
import LoginPage from './pages/authPages/LoginPage';
import Signup from './pages/authPages/SignupPage';
import CollaboratorPage from './pages/formPages/CollaboratorPage';
import EmployeeShiftRestrictionsPage from './pages/formPages/EmployeeShiftRestrictionsPage';
import GeneralPage from './pages/formPages/GeneralPage';
import SchedulePage from './pages/formPages/SchedulePage';
import StaffRequirementPage from './pages/formPages/StaffRequirementPage';
import StaffDistributionPage from './pages/formPages/StaffDistributionPage';
import ForgotPassword from './pages/authPages/ForgotPasswordPage';
import ShiftSequencePage from './pages/formPages/ShiftSequencePage';
import WeeklySumPage from './pages/formPages/WeeklySumPage';
import GenerateSchedulePage from './pages/formPages/GenerateSchedulePage';
import RosterResultPage from './pages/formPages/RosterResultPage';
import SavedScheduleDetailPage from './pages/profilePages/SavedScheduleDetailPage';
import ProfilePage from './pages/profilePages/ProfilePage';
import MySubscriptionPage from './pages/profilePages/MySubscriptionPage';
import PricingPage from './pages/headerPages/PricingPage';
import ExamplePage from './pages/headerPages/ExamplePage';
import { AlertProvider } from './context/alert/AlertProvider';
import CustomAlertProvider from './context/alert/CustomAlertProvider';
import ResetPassword from './pages/authPages/ResetPasswordPage';
import AboutUs from './pages/footerPages/AboutUsPage';
import Contacts from './pages/footerPages/ContactsPage';
import ScrollToTop from './components/utils/ScrollToTop';
import Layout from './components/ui/Layout';
import FormNavigationWrapper from './navigation/FormNavigationWrapper';
import { TranslationProvider } from './context/translations/TranslationProvider';
import { useTranslation } from 'react-i18next';
import TeamManagement from './pages/profilePages/MyTeamsPage';
import ScenarioSelectionPage from './pages/formPages/ScenarioSelectionPage';
import { ScenarioProvider } from './context/scenario/ScenarioContext';
import ScenariosManagementPage from './pages/profilePages/ScenarioManagementPage';
import ManageScenariosPage from './pages/profilePages/ManageScenariosPage';
import AbsencesPage from './pages/formPages/AbsencesPage';
import LaborLegislationPage from './pages/formPages/LaborLegislationPage';
import TransitionsPage from './pages/formPages/TransitionsPage';
import AuthProvider from './context/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ScheduleAnalysisPage from './pages/profilePages/ScheduleAnalysisPage';
import { useAppContext } from './context/AppContext';

function App() {
  const { i18n } = useTranslation();
  const { state, dispatch } = useAppContext();
  
  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem('token');
      const userJson = localStorage.getItem('user');
      
      if (token && userJson) {
        try {
          const user = JSON.parse(userJson);
          dispatch({ type: 'SET_CURRENT_USER', payload: user });
          console.log("Auth initialized from App.js");
        } catch (error) {
          console.error('Error initializing auth:', error);
        }
      }
    };
    
    initAuth();
  }, [dispatch]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return (
    <AlertProvider>
      <CustomAlertProvider>
        <AppProvider>
          <TranslationProvider>
            <ScenarioProvider>
              <Router>
                <ScrollToTop />
                <AuthProvider>
                  <FormNavigationWrapper>
                    <Layout>
                      <div className='pt-16'>
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path='/forgot-password' element={<ForgotPassword />} />
                          <Route path='/reset-password' element={<ResetPassword />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path='/pricing' element={<PricingPage />} />
                          <Route path='/example' element={<ExamplePage />} />
                          <Route path='/about-us' element={<AboutUs />} />
                          <Route path='contacts' element={<Contacts />} />
                          
                          <Route element={<ProtectedRoute />}>
                            <Route path='/scenarios' element={<ScenarioSelectionPage />} />
                            <Route path="/form/collaborators" element={<CollaboratorPage />} />
                            <Route path="/form/general" element={<GeneralPage />} />
                            <Route path="/form/schedule" element={<SchedulePage />}/>
                            <Route path="/form/legislation" element={<LaborLegislationPage />} />
                            <Route path="/form/absences" element={<AbsencesPage />} />
                            <Route path="/form/availability" element={<EmployeeShiftRestrictionsPage />} />
                            <Route path="/form/transitions" element={<TransitionsPage />}/>
                            <Route path="/form/staff" element={<StaffRequirementPage />} />
                            <Route path="/form/distribution" element={<StaffDistributionPage />}/>
                            <Route path="/form/sseq" element={<ShiftSequencePage />} />
                            <Route path="/form/wsum" element={<WeeklySumPage />}/>
                            <Route path="/form/generate" element={<GenerateSchedulePage />}/>
                            <Route path='/result' element={<RosterResultPage />}/>
                            <Route path="/schedules/:id" element={<SavedScheduleDetailPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path='/subscription' element={<MySubscriptionPage />}/>
                            <Route path='/myscenarios' element={<ScenariosManagementPage />}/>
                            <Route path='/teams' element={<TeamManagement />}/>
                            <Route path='/scenarios/manage' element={<ManageScenariosPage />}/>
                            <Route path="/schedules/:id/analysis" element={<ScheduleAnalysisPage />} />
                          </Route>
                        </Routes>
                      </div>
                    </Layout>
                  </FormNavigationWrapper>
                </AuthProvider>
              </Router>
            </ScenarioProvider>
          </TranslationProvider>
        </AppProvider>
      </CustomAlertProvider>
    </AlertProvider>
  );
}

export default App;